import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Card } from './ui/card';
import InfoTip from './InfoTip';
import { Button } from './ui/button';

const TONES = [
  { value: 'professional', label: 'Professional', description: 'Clear, confident and suitable for everyday business guidance.' },
  { value: 'friendly', label: 'Friendly', description: 'Warm and approachable while keeping the answer precise.' },
  { value: 'formal', label: 'Formal', description: 'Structured and reserved for policy-led or regulated environments.' },
  { value: 'casual', label: 'Casual', description: 'Relaxed and conversational for informal teams.' },
];

const ResponseToneSettings = forwardRef(function ResponseToneSettings(
  { showSaveAction = true, onStatusChange },
  ref,
) {
  const [tone, setTone] = useState('professional');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const savedTone = useRef('professional');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    axiosInstance.get('/system/response-tone')
      .then(({ data }) => {
        if (!active) return;
        const nextTone = data.tone || 'professional';
        savedTone.current = nextTone;
        setTone(nextTone);
      })
      .catch((error) => {
        if (active) setLoadError(error.response?.data?.detail || 'Response tone could not be loaded.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [loadAttempt]);

  const dirty = tone !== savedTone.current;

  useEffect(() => {
    onStatusChange?.({ ready: !loading && !loadError, dirty, saving });
  }, [dirty, loadError, loading, onStatusChange, saving]);

  const persist = async ({ notify = true } = {}) => {
    setSaving(true);
    try {
      await axiosInstance.put('/system/response-tone', { tone });
      savedTone.current = tone;
      if (notify) toast.success('Response tone updated');
      return true;
    } catch (error) {
      if (notify) {
        toast.error(error.response?.data?.detail || 'Failed to save response tone');
        return false;
      }
      throw error;
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    save: () => persist({ notify: false }),
    isDirty: () => dirty,
    isReady: () => !loading && !loadError,
  }), [dirty, loadError, loading, tone]);

  const retryLoad = () => {
    setLoading(true);
    setLoadError('');
    setLoadAttempt((attempt) => attempt + 1);
  };

  if (loading) return <Card className="p-8 text-center text-sm text-slate-500">Loading response tone…</Card>;

  if (loadError) {
    return (
      <Card className="border-amber-200 bg-amber-50 p-6" data-testid="response-tone-load-error">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 shrink-0 text-amber-600" size={20} />
          <div className="flex-1">
            <h2 className="font-semibold text-amber-950">Response tone is unavailable</h2>
            <p className="mt-1 text-sm text-amber-800">{loadError} Retry before changing this setting.</p>
            <Button type="button" variant="outline" className="mt-4 border-amber-300 bg-white text-amber-950" onClick={retryLoad}>
              <RefreshCw size={16} /> Retry
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6" data-testid="response-tone-settings">
      <div className="mb-4 flex items-center gap-2">
        <h2 className="text-xl font-bold">Response preferences</h2>
        <InfoTip label="About response preferences">Choose how Qflow phrases answers for this organization. Grounding, retrieval and safety rules remain managed by Qflow.</InfoTip>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span id="response-tone-label" className="text-sm font-semibold">Response tone</span>
          <InfoTip label="About response tone">
            {TONES.map((option) => <span key={option.value} className="block"><strong>{option.label}:</strong> {option.description}</span>)}
            <span className="mt-1 block">This preference applies to both internal and web-assisted answers.</span>
          </InfoTip>
        </div>
        <div role="radiogroup" aria-labelledby="response-tone-label" className="inline-flex flex-wrap gap-1 rounded-xl border p-1" style={{ borderColor: 'var(--qflow-border)' }} data-testid="tone-select">
          {TONES.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={tone === option.value}
              aria-pressed={tone === option.value}
              title={option.description}
              onClick={() => setTone(option.value)}
              disabled={saving}
              className="qflow-segment h-9 rounded-lg px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
              data-testid={`tone-option-${option.value}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {showSaveAction && <Button className="mt-5" disabled={saving || !dirty} data-testid="save-response-tone-btn" onClick={() => persist()}>
        {saving ? 'Saving…' : 'Save response tone'}
      </Button>}
    </Card>
  );
});

export default ResponseToneSettings;
